import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useEffect, useMemo, useRef} from 'react'
import {ReactComponent as DuoDoctorScope} from '../../assets/icons/DuoDoctorScope.svg'
import {ReactComponent as DuoFunnel} from '../../assets/icons/DuoFunnel.svg'
import {ReactComponent as DuoList} from '../../assets/icons/DuoList.svg'
import {ReactComponent as DuoLung} from '../../assets/icons/DuoLung.svg'
import {ReactComponent as DuoTemperature2} from '../../assets/icons/DuoTemperature2.svg'

import TranslateUtils from '../../i18n/TranslateUtils'
import {useQueryParams} from '../../utils/NavigatorUtils'
import {
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterModeChangeListener,
    removeFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'
import {
    onItemTableToolBarSelectedListener,
    tableToolBarEventTypes,
} from './broadcast/TableToolBarEvent'
import {tableToolBarDescriptionList} from './Constant'
import {useDatatableContext} from './DataTableContext'
import StyledAdvancedFilter from './StyledAdvancedFilter'
import StyledFilterByHealthCarePractitioner from './StyledFilterByHealthCarePractitioner'
import StyledFilterByPathway from './StyledFilterByPathway'
import StyledFilterBySicknessLevel from './StyledFilterBySicknessLevel'
import StyledFilterByStaticList from './StyledFilterByStaticList'
import StyledToolbarIcon from './StyledToolbarIcon'

const StyledFilterToolBar = () => {
    useSignals()

    const {tableType, initialAdvancedFilterRequest, handleClearFilterRequest} =
        useDatatableContext()
    const {translateDatatable} = TranslateUtils()

    const filterByStaticListRef = useRef(null)
    const filterByPathwayRef = useRef(null)
    const filterBySicknessLevelRef = useRef(null)
    const filterByHealthCarePractitionerRef = useRef(null)
    const filterByAdvancedRef = useRef(null)

    const clearFilterRef = useRef(null)
    const hasFilter = useSignal(
        initialAdvancedFilterRequest &&
            Object.keys(initialAdvancedFilterRequest).length > 0,
    )

    const handleClearAllFilter = () => {
        onFilterModeChangeListener(
            tableType,
            filterExplainEventTypes.CLEAR_ALL_FILTER,
        )

        if (handleClearFilterRequest) {
            handleClearFilterRequest()
        }

        window.history.replaceState(null, 'TaVIE', `/patients`)
    }

    const query = useQueryParams()
    //#region URL Query Params
    const hasQueryParamsFilter = useMemo(() => {
        return query.get('staticListId') || query.get('pathwayId')
    }, [query])
    //#endregion

    useEffect(() => {
        if (
            clearFilterRef.current &&
            initialAdvancedFilterRequest &&
            Object.keys(initialAdvancedFilterRequest).length < 1
        ) {
            if (!hasQueryParamsFilter) {
                clearFilterRef.current?.classList?.add('hidden')
            }
        }

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.HAS_FILTER,
            )}`,
            eventType => {
                if (eventType === filterExplainEventTypes.HAS_FILTER) {
                    hasFilter.value = true
                    if (clearFilterRef.current) {
                        clearFilterRef.current?.classList?.remove('hidden')
                    }
                }
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            eventType => {
                if (eventType === filterExplainEventTypes.CLEAR_ALL_FILTER) {
                    hasFilter.value = false
                    if (clearFilterRef.current) {
                        clearFilterRef.current?.classList?.add('hidden')
                    }
                }
            },
        )

        return () => {
            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.HAS_FILTER,
            )

            removeFilterExplainChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )
        }
    }, [])

    return (
        <div className="container">
            <div className="flex align-items-center justify-content-between px-2 mb-0">
                <h6 className="toggle-group-label font-semibold m-0 mb-1 ">
                    {translateDatatable('Filter')}
                </h6>
                <div
                    ref={clearFilterRef}
                    className="cursor-pointer text-gray-600 text-danger"
                    onClick={() => {
                        handleClearAllFilter()
                    }}>
                    <i className="pi pi-times mr-1"></i>
                    <span>Clear</span>
                </div>
            </div>
            <div className="custom-toolbar-items-wrapper py-0 pr-2">
                <StyledToolbarIcon
                    icon={<DuoList className="text-cyan-600" />}
                    label={translateDatatable('Static List')} //Filter by
                    description={tableToolBarDescriptionList.filterByStaticList}
                    onClick={() => {
                        filterByStaticListRef.current.showForm()

                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Static List',
                        )
                    }}
                />
                <StyledToolbarIcon
                    icon={<DuoLung className="text-indigo-700" />}
                    label={translateDatatable('Pathway')}
                    description={tableToolBarDescriptionList.filterByPathway}
                    onClick={() => {
                        filterByPathwayRef.current.showForm()

                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Pathway',
                        )
                    }}
                />

                <StyledToolbarIcon
                    icon={
                        <DuoTemperature2
                            className="text-pink-700"
                            width={23}
                            height={23}
                        />
                    }
                    label={translateDatatable('Sickness Level')}
                    description={tableToolBarDescriptionList.filterBySickness}
                    onClick={() => {
                        filterBySicknessLevelRef.current.showForm()
                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Sickness Level',
                        )
                    }}
                />

                <StyledToolbarIcon
                    icon={<DuoDoctorScope className="text-blue-500" />}
                    label={translateDatatable('HC.Practitioner')}
                    description={tableToolBarDescriptionList.filterByHCP}
                    onClick={() => {
                        filterByHealthCarePractitionerRef.current.showForm()

                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'HC.Practitioner',
                        )
                    }}
                />
                <StyledToolbarIcon
                    icon={<DuoFunnel className="text-purple-500" />}
                    label={translateDatatable('Advanced Filter')}
                    description={tableToolBarDescriptionList.advanceFilter}
                    onClick={() => {
                        filterByAdvancedRef.current.showForm()
                        onItemTableToolBarSelectedListener(
                            tableType,
                            tableToolBarEventTypes.ON_ITEM_SELECTED,
                            'Advanced Filter',
                        )
                    }}
                />
            </div>

            <hr className="toggle-toolbar-divider" />
            <StyledFilterByStaticList ref={filterByStaticListRef} />

            <StyledFilterByPathway ref={filterByPathwayRef} />

            <StyledFilterBySicknessLevel ref={filterBySicknessLevelRef} />

            <StyledFilterByHealthCarePractitioner
                ref={filterByHealthCarePractitionerRef}
            />

            <StyledAdvancedFilter ref={filterByAdvancedRef} />
        </div>
    )
}

export default StyledFilterToolBar
